import * as Safe from "safe-portals";
import { makeActionTypeDefinition } from "@flow/common/action_type";
import { thankYouEmailCustomConfig } from "@flow/core/shared/common_custom_config";
import { DonationTrackingType } from "./types";

/**
 * The donation tracking feature is a thermometer that shows the progress towards a target donation amount
 * - this is the config
 */
const DonationTrackingConfigSerializer = Safe.obj({
  enabled: Safe.bool,
  title: Safe.str,
  target_amount: Safe.float,
  target_frequency: Safe.str,
  progress_message: Safe.str,
});
export type DonationTrackingConfig = Safe.TypeIn<typeof DonationTrackingConfigSerializer>;

/**
 * The working data for the donation tracking feature - current totals.
 */
const DonationTrackingTotalsSerializer = Safe.obj({
  donation_total: Safe.float,
  oneoff_donation_total: Safe.float,
  regular_donation_total: Safe.float,
});
export type DonationTrackingTotals = Safe.TypeIn<typeof DonationTrackingTotalsSerializer>;

const DonationBoostConfigSerializer = Safe.obj({
  enabled: Safe.bool,
  default: Safe.bool,
  amount: Safe.float,
  message: Safe.str,
  info_header: Safe.str,
  info_detail: Safe.str,
});
export type DonationBoostConfig = Safe.TypeIn<typeof DonationBoostConfigSerializer>;

/**
 * This data is passed to ../frontend/member/DonateAction when this action is begun
 * it is generated in ../backend/action_type_handlers
 */

const DonateActionFrontendDataSerializer = Safe.obj({
  title: Safe.str,
  personalised_title: Safe.optional(Safe.str),
  blurb: Safe.str,
  image_url: Safe.str,
  image_alt_text: Safe.str,
  bg_image_url: Safe.str,
  bg_colour_hex: Safe.optional(Safe.str),
  donation_widget_subheading: Safe.str,
  amounts_available_to_select: Safe.array(Safe.float),
  monthly_only: Safe.bool,
  checkout_title: Safe.str,
  display_skip_donation_button: Safe.bool, // (option may be removed in future)

  // Experimental options for 'Make It Monthly' buttons
  use_buttons_for_make_it_monthly: Safe.bool, // TODO: Remove once we know whether or not this, vs the checkbox is the best way to do this
  donation_frequency_button_colour: Safe.str, // Intended for a/b testing in TTRSUPP-238 (option may be removed after testing)
  space_between_donation_frequency_buttons: Safe.bool, // Also intended for a/b testing in TTRSUPP-238 (as above)

  boost_config: DonationBoostConfigSerializer,

  tracking_config: DonationTrackingConfigSerializer,
  tracking_totals: DonationTrackingTotalsSerializer,
});

/**
 * Represents all the values the admin user can set to configure a DonateAction.
 * This is NOT the same as the values that are passed to the member facing DonateActionComponent.
 */
export const DonateActionConfigSerializer = Safe.obj({
  title: Safe.str,
  personalised_title: Safe.optional(Safe.str),
  blurb: Safe.str,
  image_url: Safe.str,
  image_alt_text: Safe.str,
  bg_image_url: Safe.str,
  bg_colour_hex: Safe.optional(Safe.str),
  donation_widget_subheading: Safe.str,
  amounts_available_to_select: Safe.array(Safe.float),
  monthly_only: Safe.bool,

  // Experimental options for 'Make It Monthly' buttons
  use_buttons_for_make_it_monthly: Safe.bool,
  donation_frequency_button_colour: Safe.str,
  space_between_donation_frequency_buttons: Safe.bool,

  checkout_title: Safe.str,
  display_skip_donation_button: Safe.bool,
  boost_config: DonationBoostConfigSerializer,
  tracking_config: DonationTrackingConfigSerializer,

  thank_you_emails: Safe.obj({
    one_off: Safe.obj({
      ...thankYouEmailCustomConfig,
    }),
    regular: Safe.obj({
      ...thankYouEmailCustomConfig,
    }),
  }),
});

const donateConfigDefaults: DonateActionConfig = {
  title: "",
  personalised_title: "",
  blurb: "",
  image_url: "https://files.38degrees.org.uk/items/files/000/003/413/original/shutterstock_663459544.jpg",
  image_alt_text: "Parliament",
  bg_image_url: "https://files.38degrees.org.uk/items/files/000/003/918/display/orange_post.png",
  bg_colour_hex: undefined,
  donation_widget_subheading: "<h4>The average donation is £8.85</h4><h4>Choose your donation amount</h4>",
  amounts_available_to_select: [3, 8.85, 10, 25, 50, 100, 200],
  monthly_only: false,

  // Experimental options for 'Make It Monthly' buttons
  use_buttons_for_make_it_monthly: false,
  donation_frequency_button_colour: "default",
  space_between_donation_frequency_buttons: false,

  checkout_title: "",
  display_skip_donation_button: false,
  boost_config: {
    enabled: false,
    default: false,
    amount: 0.48,
    message: "Boost your donation by adding £{ donation_boost } to cover transaction fees*",
    info_header: "*Transaction fees explained:",
    info_detail:
      "This covers the cost of the transaction fee for your donation, updating our records and keeping your donation secure. You can ensure that we receive the full amount by adding a small additional sum to your donation.",
  },
  tracking_config: {
    enabled: false,
    title: "Donation Tracker",
    target_amount: 10000,
    target_frequency: DonationTrackingType.ALL,
    progress_message: "of £{ target_amount } donation target raised",
  },

  thank_you_emails: {
    one_off: {
      from_name: "",
      thank_you_email_subject: "Thanks for donating!",
      thank_you_email_body: `
        <p>It's all because of supporters like you that 38 Degrees can remain independent and campaign on issues we all care about.</p>
        <p>If you have any questions about donations, please don't hesitate to drop us a line on donate@38degrees.org.uk. A member of the 38 Degrees office team will be happy to help!</p>
        `,
      thank_you_email_footer: "",
    },
    regular: {
      from_name: "",
      thank_you_email_subject: "Thanks for donating",
      thank_you_email_body: `
        <p>Thank you so much for your kind donation.</p>
        <p>It's all because of supporters like you that 38 Degrees can remain independent and campaign on issues we all care about.</p>
        <p>If you have any questions about donations, please don't hesitate to drop us a line on donate@38degrees.org.uk - A member of the 38 Degrees office team will be happy to help!</p>
        `,
      thank_you_email_footer:
        "<p>PS: Just in case you're interested, the 38 Degrees office team is always transparent about how your donations are spent. If you fancy a riveting read over a cup of tea, you can have a look at our full audited accounts: https://home.38degrees.org.uk/about/#donation-policy</p>",
    },
  },
};

function checkDonateConfigValid(config: DonateActionConfig) {
  const errors: Record<string, string> = {};
  // Donate config validation
  if (config.title.length === 0) {
    errors.title = "Title is required";
  }
  if (config.personalised_title && checkIfPersonalisedTitleContainsMergeTag(config.personalised_title)) {
    errors.personalisedTitle = "Error - Please check the merge tag { member_name } is formatted correctly";
  }
  if (config.checkout_title.length === 0) {
    errors.checkout_title = "Checkout title is required";
  }
  return { valid: Object.keys(errors).length === 0, errors: errors };
}

function checkIfPersonalisedTitleContainsMergeTag(personalisedTitle: string) {
  if (personalisedTitle.length > 0) {
    return !personalisedTitle.includes("{ member_name }");
  }
  return true;
}

export const DonateAction_def = makeActionTypeDefinition({
  plugin: "donate",
  // action type identifier. don't change this without migrating data
  action_type: "donate:DonateAction",
  supports_a_b_tests: true,
  custom_config_type: DonateActionConfigSerializer,
  // these are the default action properties when a new DonateAction is created
  custom_config_defaults: donateConfigDefaults,
  custom_data_type: DonateActionFrontendDataSerializer,
  check_valid: checkDonateConfigValid,
});

export type DonateActionConfig = Safe.TypeIn<typeof DonateActionConfigSerializer>;
export type DonateActionCustomData = Safe.TypeIn<typeof DonateActionFrontendDataSerializer>;
